// GA-inspired, table-based HTML email template (inline styles only — email-client safe)
import { MONTH_FULL, formatCompact, formatNumber } from "./utils";

const pill = (change, lowerIsBetter = false) => {
  if (change === null || change === undefined)
    return `<span style="display:inline-block;padding:3px 10px;border-radius:12px;background:#F1F3F4;color:#5F6368;font-size:12px;font-weight:600;">–</span>`;
  const isUp = change >= 0;
  const good = lowerIsBetter ? !isUp : isUp;
  const bg = good ? "#E6F4EA" : "#FCE8E6";
  const color = good ? "#137333" : "#C5221F";
  const arrow = isUp ? "&#8593;" : "&#8595;";
  return `<span style="display:inline-block;padding:3px 10px;border-radius:12px;background:${bg};color:${color};font-size:12px;font-weight:700;">${arrow} ${Math.abs(change).toFixed(1)}%</span>`;
};

const textToHtml = (text, color = "#3C4043") => {
  if (!text) return "";
  return text
    .split("\n")
    .filter((l) => l.trim())
    .map((l) => {
      const line = l.trim();
      if (/^(insights|next steps):?$/i.test(line))
        return `<p style="margin:14px 0 6px;font-size:14px;font-weight:700;color:#202124;">${line.replace(/:$/, "")}</p>`;
      if (line.startsWith("-") || line.startsWith("•"))
        return `<p style="margin:5px 0;font-size:14px;line-height:1.6;color:${color};">&#8226; ${line.replace(/^[-•]\s*/, "")}</p>`;
      return `<p style="margin:8px 0;font-size:14px;line-height:1.6;color:${color};">${line}</p>`;
    })
    .join("");
};

// kpis: [{label, display, mom, yoy, lowerIsBetter}]
const kpiCardsHtml = (kpis) => {
  const card = (k) => `
    <td width="33%" style="padding:6px;" valign="top">
      <table width="100%" cellpadding="0" cellspacing="0" style="border:1px solid #DADCE0;border-radius:12px;">
        <tr><td align="center" style="padding:20px 8px 16px;">
          <div style="font-size:13px;color:#5F6368;font-weight:600;margin-bottom:10px;">${k.label}</div>
          <div style="font-size:30px;font-weight:800;color:#202124;letter-spacing:-0.5px;margin-bottom:10px;">${k.display}</div>
          <div>${pill(k.mom, k.lowerIsBetter)}</div>
          ${k.yoy !== null && k.yoy !== undefined ? `<div style="font-size:11px;color:#5F6368;margin-top:6px;">vs last year: <span style="color:${(k.lowerIsBetter ? k.yoy < 0 : k.yoy >= 0) ? "#137333" : "#C5221F"};font-weight:700;">${k.yoy >= 0 ? "+" : ""}${k.yoy.toFixed(1)}%</span></div>` : ""}
        </td></tr>
      </table>
    </td>`;
  const rows = [];
  for (let i = 0; i < kpis.length; i += 3) {
    const chunk = kpis.slice(i, i + 3);
    while (chunk.length < 3 && kpis.length > 3) chunk.push(null);
    rows.push(`<tr>${chunk.map((k) => (k ? card(k) : `<td width="33%" style="padding:6px;"></td>`)).join("")}</tr>`);
  }
  return `<table width="100%" cellpadding="0" cellspacing="0">${rows.join("")}</table>`;
};

// channels: [{name, revenue, mix, mixShift, sessions, convRate, revMom}]
const channelTableHtml = (channels, currency) => {
  const th = (t, align = "right") =>
    `<td style="padding:10px 12px;font-size:12px;font-weight:700;color:#5F6368;text-align:${align};border-bottom:2px solid #E8EAED;">${t}</td>`;
  const rows = channels
    .map((c, i) => {
      const bg = i % 2 === 0 ? "#FFFFFF" : "#F8F9FA";
      const momHtml = c.revMom === null || c.revMom === undefined
        ? `<span style="color:#9AA0A6;">–</span>`
        : `<span style="color:${c.revMom >= 0 ? "#137333" : "#C5221F"};font-weight:700;">${c.revMom >= 0 ? "+" : ""}${c.revMom.toFixed(1)}%</span>`;
      const mixHtml = c.mix === null || c.mix === undefined
        ? "–"
        : `${c.mix.toFixed(1)}%${c.mixShift !== null && c.mixShift !== undefined ? ` <span style="font-size:11px;color:${c.mixShift >= 0 ? "#137333" : "#C5221F"};">(${c.mixShift >= 0 ? "+" : ""}${c.mixShift.toFixed(1)}pt)</span>` : ""}`;
      return `<tr style="background:${bg};">
        <td style="padding:12px;font-size:13px;font-weight:600;color:#202124;">${c.name}</td>
        <td style="padding:12px;font-size:13px;text-align:right;font-weight:700;color:#202124;">${formatCompact(c.revenue, { isCurrency: true, currency })}</td>
        <td style="padding:12px;font-size:13px;text-align:right;color:#3C4043;">${mixHtml}</td>
        <td style="padding:12px;font-size:13px;text-align:right;color:#3C4043;">${c.sessions !== null ? formatNumber(c.sessions) : "–"}</td>
        <td style="padding:12px;font-size:13px;text-align:right;">${momHtml}</td>
      </tr>`;
    })
    .join("");
  return `<table width="100%" cellpadding="0" cellspacing="0" style="background:#ffffff;border-radius:12px;overflow:hidden;">
    <tr>${th("Channel", "left")}${th("Revenue")}${th("% Mix")}${th("Sessions")}${th("Rev MoM")}</tr>
    ${rows}
  </table>`;
};

export const DEFAULT_BRANDING = {
  brandName: "AGCO",
  brandSuffix: "Reports Hub",
  headerTag: "Ecommerce Analytics",
  heroTitle: "Your ecommerce performance report for",
  footerSignoff: "Happy measuring,",
  footerTeam: "The AGCO Digital Team",
};

export const buildEmailHtml = ({ config, kpis, channels, summary, insights, charts, branding, organic, findings }) => {
  const b = { ...DEFAULT_BRANDING, ...(branding || {}) };
  const monthFull = MONTH_FULL[config.month] || config.month;
  const period = `${monthFull} ${config.year}`;
  const chartsHtml = charts && (charts.trend || charts.donut)
    ? `<tr><td style="padding:8px 40px 24px;">
        ${charts.trend ? `<img src="${charts.trend}" width="560" alt="Revenue trend" style="display:block;width:100%;max-width:560px;border:1px solid #E8EAED;border-radius:12px;margin-bottom:16px;"/>` : ""}
        ${charts.donut ? `<img src="${charts.donut}" width="560" alt="Channel mix" style="display:block;width:100%;max-width:560px;border:1px solid #E8EAED;border-radius:12px;"/>` : ""}
      </td></tr>`
    : "";

  return `<!DOCTYPE html>
<html><head><meta charset="utf-8"/><meta name="viewport" content="width=device-width,initial-scale=1"/></head>
<body style="margin:0;padding:0;background:#F1F3F4;font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;">
<table width="100%" cellpadding="0" cellspacing="0" style="background:#F1F3F4;padding:24px 8px;"><tr><td align="center">
<table width="640" cellpadding="0" cellspacing="0" style="background:#ffffff;border-radius:16px;overflow:hidden;max-width:640px;width:100%;">

  <!-- Header -->
  <tr><td style="padding:24px 40px;border-bottom:1px solid #E8EAED;">
    <table width="100%" cellpadding="0" cellspacing="0"><tr>
      <td style="font-size:19px;font-weight:800;color:#202124;letter-spacing:-0.3px;">
        <span style="color:#188038;">&#9679;</span>&nbsp;${b.brandName} <span style="font-weight:500;color:#5F6368;">${b.brandSuffix}</span>
      </td>
      <td align="right" style="font-size:13px;color:#5F6368;font-weight:600;">${b.headerTag}</td>
    </tr></table>
  </td></tr>

  <!-- Hero -->
  <tr><td align="center" style="padding:44px 48px 8px;">
    <h1 style="margin:0;font-size:32px;line-height:1.25;font-weight:800;color:#202124;letter-spacing:-0.5px;">
      ${b.heroTitle}<br/><span style="color:#E37400;">${period}</span>
    </h1>
    ${config.country ? `<div style="margin-top:12px;display:inline-block;padding:5px 16px;border-radius:16px;background:#E8F0FE;color:#1A73E8;font-size:13px;font-weight:700;">${config.country}</div>` : ""}
  </td></tr>

  <!-- Summary -->
  <tr><td align="center" style="padding:12px 56px 28px;">
    ${summary ? textToHtml(summary, "#5F6368") : `<p style="margin:0;font-size:15px;line-height:1.6;color:#5F6368;">Review the latest metrics for your business. See what's working and get action items for growth.</p>`}
  </td></tr>

  <!-- KPI cards -->
  <tr><td style="padding:0 34px 24px;">${kpiCardsHtml(kpis)}</td></tr>

  <!-- Key movements (data findings) -->
  ${findings ? `<tr><td style="padding:0 24px 24px;">
    <table width="100%" cellpadding="0" cellspacing="0" style="background:#F8F9FA;border:1px solid #E8EAED;border-radius:16px;">
      <tr><td style="padding:24px 32px;">
        <h2 style="margin:0 0 10px;font-size:16px;font-weight:800;color:#202124;">Key movements</h2>
        ${textToHtml(findings)}
      </td></tr>
    </table>
  </td></tr>` : ""}

  ${chartsHtml}

  <!-- Channel performance -->
  ${channels && channels.length ? `<tr><td style="padding:8px 24px 24px;">
    <table width="100%" cellpadding="0" cellspacing="0" style="background:#E8F0FE;border-radius:16px;">
      <tr><td style="padding:28px 24px;">
        <h2 style="margin:0 0 4px;font-size:20px;font-weight:800;color:#202124;text-align:center;">Channel performance</h2>
        <p style="margin:0 0 18px;font-size:13px;color:#5F6368;text-align:center;">Revenue, mix and traffic by acquisition channel.</p>
        ${channelTableHtml(channels, config.currency)}
      </td></tr>
    </table>
  </td></tr>` : ""}

  <!-- Organic performance -->
  ${organic ? `<tr><td style="padding:0 24px 24px;">
    <table width="100%" cellpadding="0" cellspacing="0" style="background:#E6F4EA;border-radius:16px;">
      <tr><td style="padding:28px 24px;">
        <h2 style="margin:0 0 4px;font-size:20px;font-weight:800;color:#202124;text-align:center;">Organic performance</h2>
        <p style="margin:0 0 18px;font-size:13px;color:#5F6368;text-align:center;">Organic Search revenue and traffic over time.</p>
        <table width="100%" cellpadding="0" cellspacing="0"><tr>
          <td width="50%" style="padding:0 6px 12px 0;">
            <table width="100%" cellpadding="0" cellspacing="0" style="background:#ffffff;border-radius:12px;"><tr><td align="center" style="padding:16px 8px;">
              <div style="font-size:12px;color:#5F6368;font-weight:600;margin-bottom:6px;">Organic Revenue</div>
              <div style="font-size:24px;font-weight:800;color:#202124;margin-bottom:8px;">${formatCompact(organic.revenue, { isCurrency: true, currency: config.currency })}</div>
              ${pill(organic.revMom)}
            </td></tr></table>
          </td>
          <td width="50%" style="padding:0 0 12px 6px;">
            <table width="100%" cellpadding="0" cellspacing="0" style="background:#ffffff;border-radius:12px;"><tr><td align="center" style="padding:16px 8px;">
              <div style="font-size:12px;color:#5F6368;font-weight:600;margin-bottom:6px;">Organic Sessions</div>
              <div style="font-size:24px;font-weight:800;color:#202124;margin-bottom:8px;">${formatNumber(organic.sessions)}</div>
              ${pill(organic.sesMom)}
            </td></tr></table>
          </td>
        </tr></table>
        ${organic.chart ? `<img src="${organic.chart}" width="560" alt="Organic trend" style="display:block;width:100%;max-width:560px;background:#ffffff;border-radius:12px;"/>` : ""}
      </td></tr>
    </table>
  </td></tr>` : ""}

  <!-- Insights -->
  ${insights ? `<tr><td style="padding:0 24px 28px;">
    <table width="100%" cellpadding="0" cellspacing="0" style="background:#FEF7E0;border-radius:16px;">
      <tr><td style="padding:28px 32px;">
        <h2 style="margin:0 0 10px;font-size:20px;font-weight:800;color:#202124;text-align:center;">Strategic insights</h2>
        ${textToHtml(insights)}
      </td></tr>
    </table>
  </td></tr>` : ""}

  <!-- Footer -->
  <tr><td style="padding:8px 40px 32px;">
    <div style="height:4px;width:180px;border-radius:2px;background:#188038;margin-bottom:18px;"></div>
    <p style="margin:0;font-size:14px;color:#3C4043;">${b.footerSignoff}</p>
    <p style="margin:4px 0 0;font-size:14px;font-weight:800;color:#202124;">${b.footerTeam}</p>
    <p style="margin:16px 0 0;font-size:11px;color:#9AA0A6;">Generated with ${b.brandName} ${b.brandSuffix} &#8226; ${period}${config.country ? ` &#8226; ${config.country}` : ""}</p>
  </td></tr>

</table>
</td></tr></table>
</body></html>`;
};
